import { useState, useEffect, useRef } from 'react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { FormItem, FormLabel, FormMessage } from '@rocco/components/form';
import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import { cn } from '@rocco/utils/cn';
import { AvatarCircles } from '@rocco/components/avatar-circles';
import { getCollaboratorAvatar } from '@modules/build-hub/utils/get-collaborator-avatar';
import { HousePlus, Check, ChevronDown, Pencil } from 'lucide-react';
import { useRootData } from '@modules/root';
import { getGoogleStaticMapForBuildHub } from '@modules/build-hub/utils/get-google-static-map';
import { BuildHubInlinePreview } from './BuildHubInlinePreview';
import type { BuildHubFieldSelection } from '../hooks/use-build-hub-field-selection';
import { InlineCreateBuildHubForm } from './InlineCreateBuildHubForm';
import { BUILD_HUB_STAGE_CONFIG_API_ROUTE } from '~/routes/remix-api.build-hub.stage-config';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';
import { useUserLoggedIn } from '@modules/user';

interface BuildHubSelectorProps {
    /** The currently selected BuildHub ID. */
    value?: string;
    /** Callback when a BuildHub is selected. */
    onChange: (value: string | undefined) => void;
    /** Whether the selector is disabled. */
    disabled?: boolean;
    /** Additional className for the FormItem container. */
    className?: string;
    /** Callback to save form state when selection changes. */
    onBlur?: () => void;
    /** List of available BuildHub projects. */
    buildHubs?: BuildHubOption[];
    /** Whether the list is loading. */
    isLoading: boolean;
    /** Current field selection state. */
    fieldSelection: BuildHubFieldSelection;
    /** Callback when field selection changes. */
    onFieldSelectionChange: (selection: BuildHubFieldSelection) => void;
    /** Callback to refetch the buildHubs list. */
    onRefetchBuildHubs?: () => void;
}

/**
 * A popover-based selector for choosing a BuildHub project to attach to an enquiry.
 * Opens a dropdown below the trigger that displays the user's BuildHub projects with rich information.
 * Clicking on a project selects it and closes the dropdown. Clicking a selected project deselects it.
 */
export function BuildHubSelector({
    value,
    onChange,
    disabled = false,
    className,
    onBlur,
    buildHubs,
    isLoading,
    fieldSelection,
    onFieldSelectionChange,
    onRefetchBuildHubs,
}: BuildHubSelectorProps) {
    const { isLoggedIn } = useUserLoggedIn();
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<'list' | 'create'>('list');
    const [stageConfig, setStageConfig] = useState<StageConfiguration | null>(
        null
    );
    const [stageConfigError, setStageConfigError] = useState<string | null>(
        null
    );
    const [pendingBuildHubId, setPendingBuildHubId] = useState<string | null>(
        null
    );
    const { config } = useRootData();

    // Store onChange in a ref to avoid including it in dependency arrays
    // This prevents unnecessary re-renders when the parent recreates the function
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Preload stageConfig as soon as component mounts
    useEffect(() => {
        fetch(BUILD_HUB_STAGE_CONFIG_API_ROUTE)
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to load configuration');
                }
                return res.json();
            })
            .then((data) => {
                setStageConfig(data);
            })
            .catch((err) => {
                setStageConfigError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to load configuration'
                );
            });
    }, []);

    // Reset to list view when popover closes while in create mode
    useEffect(() => {
        if (!isOpen && view === 'create') {
            setView('list');
        }
    }, [isOpen, view]);

    // Handle BuildHub creation success
    const handleBuildHubCreated = (buildHubId: string) => {
        // Select the newly created BuildHub immediately
        onChange(buildHubId);
        // Switch back to list view
        setView('list');
        // Track the pending BuildHub ID so we can wait for it to appear in the list
        setPendingBuildHubId(buildHubId);
        // Refetch the buildHubs list to include the newly created project
        onRefetchBuildHubs?.();
    };

    // Find the selected project to display its title in the trigger
    // Use String() to ensure type consistency in comparison
    // Only compare if value is defined to avoid converting undefined to "undefined"
    const selectedProject = value
        ? buildHubs?.find((p) => String(p.ID) === String(value))
        : undefined;

    // Close popover once the newly created BuildHub appears in the list
    // Only close after refetch completes (isLoading is false) and BuildHub is found AND selected
    useEffect(() => {
        if (pendingBuildHubId && !isLoading && buildHubs) {
            // Ensure the value is set first
            // Only compare if value is defined to avoid converting undefined to "undefined"
            if (!value || String(value) !== String(pendingBuildHubId)) {
                onChangeRef.current(pendingBuildHubId);
                // Wait for the value to update before checking for selectedProject
                return;
            }

            // Check if the BuildHub is in the list
            const foundBuildHub = buildHubs.find(
                (p) => String(p.ID) === String(pendingBuildHubId)
            );

            // Also verify that selectedProject is set (value matches a BuildHub in the list)
            if (foundBuildHub && selectedProject) {
                // BuildHub is now in the list, value is set, and selectedProject is found
                // Close the popover after a small delay to ensure preview renders
                const timeout = setTimeout(() => {
                    setPendingBuildHubId(null);
                    setIsOpen(false);
                }, 200);

                return () => clearTimeout(timeout);
            }
        }
    }, [pendingBuildHubId, buildHubs, isLoading, value, selectedProject]);

    // Handle back to list - reset view state
    const handleBackToList = () => {
        setView('list');
        // Reset stage config state when going back (will refetch if needed)
        // Keep stageConfig cached for better UX
    };

    // Handle project selection
    const handleProjectSelect = (projectId: string) => {
        // If clicking on already selected project, deselect it
        if (value === projectId) {
            onChange(undefined);
        } else {
            onChange(projectId);
        }
        //setIsOpen(false);
        onBlur?.();
    };

    if (!isLoggedIn) return null;

    return (
        <FormItem className={cn('md:col-span-2', className)}>
            <FormLabel className="text-base" errorVariant="errorDarker">
                {`Include your Project Details`}
            </FormLabel>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        disabled={disabled || isLoading}
                        className="flex h-12 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-4 py-2 text-left text-base transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <span
                            className={
                                selectedProject
                                    ? 'text-gray-900'
                                    : 'text-gray-500'
                            }
                        >
                            {selectedProject
                                ? selectedProject.Title
                                : 'Select or Add a Project'}
                        </span>
                        <ChevronDown
                            className={cn(
                                'h-5 w-5 text-gray-400 transition-transform',
                                isOpen && 'rotate-180'
                            )}
                        />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    sideOffset={4}
                >
                    {view === 'list' ? (
                        <div className="flex max-h-[400px] flex-col overflow-hidden rounded-md border border-gray-200 bg-white px-2 py-3 shadow-lg">
                            {/* Add a New Project button */}
                            <button
                                type="button"
                                onClick={() => setView('create')}
                                className="mx-2 flex w-fit items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-gray-100"
                            >
                                <span className="text-base font-medium text-gray-700">
                                    Add a New Project
                                </span>
                                <Pencil className="h-4.5 w-4.5 text-gray-700" />
                            </button>

                            {/* Project list */}
                            <div className="mb-6 max-h-[350px] overflow-y-auto px-3 py-2">
                                {buildHubs?.map((project) => {
                                    const isSelected = value === project.ID;

                                    // Generate map fallback for thumbnail (similar to BuildHubListItem)
                                    const mapFallbackUrl =
                                        !project.Thumbnail && project.Location
                                            ? getGoogleStaticMapForBuildHub(
                                                  project.Location,
                                                  config.pixels
                                                      .websiteGoogleMapKey,
                                                  {
                                                      width: 80,
                                                      height: 80,
                                                      zoomLevel: 13,
                                                      showMarker: false,
                                                  }
                                              )
                                            : null;

                                    return (
                                        <button
                                            key={project.ID}
                                            type="button"
                                            onClick={() =>
                                                handleProjectSelect(project.ID)
                                            }
                                            className={cn(
                                                'mb-2 flex w-full items-center gap-3 rounded-md bg-[#f6f6f6] px-4 py-3 text-left transition-colors last:mb-0 hover:bg-gray-100',
                                                isSelected &&
                                                    'shadow-[0_1px_8px_0_rgba(0,0,0,0.25)]'
                                            )}
                                        >
                                            {/* Thumbnail with map fallback */}
                                            <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-md bg-gray-200 md:h-20 md:w-20">
                                                {project.Thumbnail ? (
                                                    <img
                                                        src={project.Thumbnail}
                                                        alt={project.Title}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : mapFallbackUrl ? (
                                                    <img
                                                        src={mapFallbackUrl}
                                                        alt={`Map of ${project.Location}`}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-full w-full items-center justify-center text-gray-400">
                                                        <HousePlus className="h-6 w-6" />
                                                    </div>
                                                )}
                                                {/* Selected indicator - brick checkmark overlay */}
                                                {isSelected && (
                                                    <div className="absolute left-0 top-0 h-full w-full bg-black/20">
                                                        <div className="flex h-full w-full items-center justify-center">
                                                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#D36D48] md:h-10 md:w-10">
                                                                <Check className="size-4 text-black md:h-6 md:w-6" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Text Info */}
                                            <div className="flex min-w-0 flex-1 flex-col">
                                                <span className="truncate text-base font-medium text-gray-900">
                                                    {project.Title}
                                                </span>
                                                <span className="truncate text-sm text-gray-500">
                                                    {project.ProjectScope ||
                                                        project.ProjectType ||
                                                        'Residential New Build'}
                                                </span>
                                            </div>

                                            {/* Collaborators */}
                                            {project.Collaborators &&
                                                project.Collaborators.length >
                                                    0 && (
                                                    <div className="ml-2 hidden flex-shrink-0 md:block">
                                                        <AvatarCircles
                                                            avatarUrls={project.Collaborators.map(
                                                                (c) => ({
                                                                    imageUrl:
                                                                        getCollaboratorAvatar(
                                                                            c
                                                                        ),
                                                                    name:
                                                                        c.name ||
                                                                        c.email,
                                                                })
                                                            )}
                                                            maxAvatars={3}
                                                            size="square40"
                                                        />
                                                    </div>
                                                )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col overflow-y-scroll rounded-md border border-gray-200 bg-white px-2 py-3 shadow-lg md:max-h-[400px]">
                            {stageConfigError && (
                                <div className="p-6 text-center text-red-600">
                                    Error loading form: {stageConfigError}
                                </div>
                            )}
                            {stageConfig && (
                                <InlineCreateBuildHubForm
                                    stageConfig={stageConfig}
                                    onBuildHubCreated={handleBuildHubCreated}
                                    onBackToList={handleBackToList}
                                />
                            )}
                            {!stageConfig && !stageConfigError && (
                                <div className="p-6 text-center text-gray-500">
                                    Loading form...
                                </div>
                            )}
                        </div>
                    )}
                </PopoverContent>
            </Popover>
            {!selectedProject && (
                <div className="mt-2 text-base text-gray-500">
                    *Including your Project Details is not required, but it
                    increases reply rates and response quality. This is to
                    provide context to your enquiry.
                </div>
            )}
            {selectedProject && (
                <BuildHubInlinePreview
                    buildHub={selectedProject}
                    fieldSelection={fieldSelection}
                    onFieldSelectionChange={onFieldSelectionChange}
                />
            )}
            <FormMessage variant="darker" />
        </FormItem>
    );
}
